import { BRANCHES } from "../data/branches";
import type { DataRow, DatasetSummary } from "../types";

export type AiDayType = "weekday" | "saturday" | "sunday";
export type AiGroupBy = "branch" | "dma" | "workType" | "status" | "hour" | "dayType" | "date";
export type AiMetric = "rows" | "distinctJobs";

export type AnalysisPlan = {
  title: string;
  intent: string;
  filters: {
    dateFrom: string | null;
    dateTo: string | null;
    workTypes: string[];
    statuses: string[];
    teams: string[];
    branchNumbers: string[];
    dmaCodes: string[];
    hourFrom: number | null;
    hourTo: number | null;
    dayTypes: AiDayType[];
  };
  groupBy: AiGroupBy;
  metric: AiMetric;
  sort: "asc" | "desc";
  limit: number;
  visualization: "bar" | "table" | "line";
  explanation: string[];
  warnings: string[];
};

export type DatasetProfile = {
  datasetVersion: string;
  dateFrom: string | null;
  dateTo: string | null;
  rowCount: number;
  workTypes: string[];
  statuses: string[];
  teams: string[];
  branchNumbers: string[];
  dmaCodes: string[];
};

export type AnalysisEvidence = {
  totalRows: number;
  distinctJobs: number;
  dateFrom: string | null;
  dateTo: string | null;
  missingDma: number;
  missingTime: number;
  groups: { label: string; value: number; rows: number; distinctJobs: number }[];
  dayTypeCounts: { weekday: number; saturday: number; sunday: number };
  peakHour: { hour: number; rows: number } | null;
};

function values(rows: DataRow[], key: keyof DataRow, max = 120): string[] {
  return [...new Set(rows.map((row) => row[key]).filter((value): value is string => typeof value === "string" && Boolean(value.trim())).map((value) => value.trim()))]
    .sort((a, b) => a.localeCompare(b, "th"))
    .slice(0, max);
}

export function buildDatasetProfile(rows: DataRow[], summary: DatasetSummary): DatasetProfile {
  return {
    datasetVersion: summary.version,
    dateFrom: summary.dateFrom === "ไม่ระบุ" ? null : summary.dateFrom,
    dateTo: summary.dateTo === "ไม่ระบุ" ? null : summary.dateTo,
    rowCount: rows.length,
    workTypes: values(rows, "workType"),
    statuses: values(rows, "status"),
    teams: values(rows, "team"),
    branchNumbers: BRANCHES.map((branch) => branch.number),
    dmaCodes: values(rows, "dmaCode", 300),
  };
}

function datePart(row: DataRow): string | null {
  const value = row.receivedAt || row.openedAt;
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? null;
}

function hourPart(row: DataRow): number | null {
  const value = row.receivedTime || row.receivedAt || row.openedAt;
  const match = String(value ?? "").match(/(?:T|\s|^)(\d{1,2}):\d{2}/);
  if (!match) return null;
  const hour = Number(match[1]);
  return hour >= 0 && hour <= 23 ? hour : null;
}

export function dayTypeForDate(date: string | null): AiDayType | null {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const day = new Date(`${date}T12:00:00+07:00`).getUTCDay();
  if (day === 6) return "saturday";
  if (day === 0) return "sunday";
  return "weekday";
}

function includesOrAll(selected: string[], value: string | null | undefined): boolean {
  return selected.length === 0 || (Boolean(value) && selected.includes(String(value)));
}

function filterRows(rows: DataRow[], plan: AnalysisPlan): DataRow[] {
  const { filters } = plan;
  return rows.filter((row) => {
    const date = datePart(row);
    const hour = hourPart(row);
    const dayType = dayTypeForDate(date);
    if (filters.dateFrom && (!date || date < filters.dateFrom)) return false;
    if (filters.dateTo && (!date || date > filters.dateTo)) return false;
    if (!includesOrAll(filters.workTypes, row.workType)) return false;
    if (!includesOrAll(filters.statuses, row.status)) return false;
    if (!includesOrAll(filters.teams, row.team)) return false;
    if (!includesOrAll(filters.branchNumbers, row.branchNumber)) return false;
    if (!includesOrAll(filters.dmaCodes, row.dmaCode)) return false;
    if (filters.dayTypes.length && (!dayType || !filters.dayTypes.includes(dayType))) return false;
    if (filters.hourFrom !== null && (hour === null || hour < filters.hourFrom)) return false;
    if (filters.hourTo !== null && (hour === null || hour > filters.hourTo)) return false;
    return true;
  });
}

function groupLabel(row: DataRow, groupBy: AiGroupBy): string {
  if (groupBy === "branch") return row.branchNumber ? `${row.branchNumber} · ${row.branchShortName || row.branchFullName || "ไม่ระบุชื่อ"}` : "ไม่ระบุสาขา";
  if (groupBy === "dma") return row.dmaCode || "ไม่ระบุ DMA";
  if (groupBy === "workType") return row.workType || "ไม่ระบุลักษณะงาน";
  if (groupBy === "status") return row.status || "ไม่ระบุสถานะ";
  if (groupBy === "hour") { const hour = hourPart(row); return hour === null ? "ไม่ระบุเวลา" : `${String(hour).padStart(2, "0")}:00–${String(hour).padStart(2, "0")}:59`; }
  if (groupBy === "dayType") return ({ weekday: "วันธรรมดา", saturday: "วันเสาร์", sunday: "วันอาทิตย์" } as const)[dayTypeForDate(datePart(row)) ?? "weekday"];
  return datePart(row) || "ไม่ระบุวันที่";
}

function distinctJobs(rows: DataRow[]): number {
  return new Set(rows.map((row) => row.jobId).filter(Boolean)).size;
}

export function executeAnalysisPlan(rows: DataRow[], plan: AnalysisPlan): AnalysisEvidence {
  const filtered = filterRows(rows, plan);
  const grouped = new Map<string, DataRow[]>();
  filtered.forEach((row) => {
    const label = groupLabel(row, plan.groupBy);
    grouped.set(label, [...(grouped.get(label) ?? []), row]);
  });
  const groups = [...grouped].map(([label, groupRows]) => {
    const jobs = distinctJobs(groupRows);
    return { label, value: plan.metric === "rows" ? groupRows.length : jobs, rows: groupRows.length, distinctJobs: jobs };
  }).sort((a, b) => plan.sort === "asc" ? a.value - b.value : b.value - a.value).slice(0, plan.limit);
  const dates = filtered.map(datePart).filter((value): value is string => Boolean(value)).sort();
  const dayTypeCounts = { weekday: 0, saturday: 0, sunday: 0 };
  const hours = new Map<number, number>();
  filtered.forEach((row) => {
    const type = dayTypeForDate(datePart(row));
    if (type) dayTypeCounts[type] += 1;
    const hour = hourPart(row);
    if (hour !== null) hours.set(hour, (hours.get(hour) ?? 0) + 1);
  });
  const peakHourEntry = [...hours].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0];
  return {
    totalRows: filtered.length,
    distinctJobs: distinctJobs(filtered),
    dateFrom: dates[0] ?? null,
    dateTo: dates.at(-1) ?? null,
    missingDma: filtered.filter((row) => !row.dmaCode).length,
    missingTime: filtered.filter((row) => hourPart(row) === null).length,
    groups,
    dayTypeCounts,
    peakHour: peakHourEntry ? { hour: peakHourEntry[0], rows: peakHourEntry[1] } : null,
  };
}
